/**
 * Analytics Event Registry
 *
 * Single source of truth for ALL telemetry event names across the product.
 * Every trackEvent call MUST use a constant from this file to prevent
 * typos and ensure consistent naming.
 *
 * Naming convention: {domain}_{action} (snake_case)
 */

// ─── Auth ────────────────────────────────────────────────────────────────

export const AuthEvents = {
  SIGN_IN_ATTEMPTED: 'auth_sign_in_attempted',
  SIGN_IN_SUCCEEDED: 'auth_sign_in_succeeded',
  SIGN_IN_FAILED: 'auth_sign_in_failed',
  SIGN_UP_ATTEMPTED: 'auth_sign_up_attempted',
  SIGN_UP_SUCCEEDED: 'auth_sign_up_succeeded',
  SIGN_UP_FAILED: 'auth_sign_up_failed',
  GOOGLE_SIGN_IN_CLICKED: 'auth_google_sign_in_clicked',
  MICROSOFT_SIGN_IN_CLICKED: 'auth_microsoft_sign_in_clicked',
  OAUTH_CLICKED: 'auth_oauth_clicked',
  OAUTH_SUCCEEDED: 'auth_oauth_succeeded',
  OAUTH_FAILED: 'auth_oauth_failed',
  OTP_SUBMITTED: 'auth_otp_submitted',
  OTP_VERIFIED: 'auth_otp_verified',
  OTP_FAILED: 'auth_otp_failed',
  VERIFICATION_RESENT: 'auth_verification_resent',
  FORGOT_PASSWORD_SUBMITTED: 'auth_forgot_password_submitted',
  PASSWORD_RESET_SUCCEEDED: 'auth_password_reset_succeeded',
  PASSWORD_RESET_FAILED: 'auth_password_reset_failed',
  MFA_CHALLENGE_REQUIRED: 'auth_mfa_challenge_required',
  MFA_VERIFY_SUCCEEDED: 'auth_mfa_verify_succeeded',
  MFA_VERIFY_FAILED: 'auth_mfa_verify_failed',
  SIGN_OUT: 'auth_sign_out',
} as const;

// ─── Documents ───────────────────────────────────────────────────────────

export const DocumentEvents = {
  CREATED: 'document_created',
  UPDATED: 'document_updated',
  DELETED: 'document_deleted',
  VIEWED: 'document_viewed',
  UPLOAD_STARTED: 'document_upload_started',
  UPLOAD_COMPLETED: 'document_upload_completed',
  UPLOAD_FAILED: 'document_upload_failed',
  UPLOAD_RETRIED: 'document_upload_retried',
  DOWNLOADED: 'document_downloaded',
  SHARED: 'document_shared',
  BULK_ACTION: 'document_bulk_action',
  SORT_CHANGED: 'document_sort_changed',
  FILTER_APPLIED: 'document_filter_applied',
  PROCESSING_COMPLETED: 'document_processing_completed',
  PROCESSING_FAILED: 'document_processing_failed',
  METADATA_EDITED: 'document_metadata_edited',
  COMMENT_ADDED: 'document_comment_added',
  VERSION_VIEWED: 'document_version_viewed',
  UPLOAD_RATE_LIMITED: 'document_upload_rate_limited',
  UPLOAD_SIZE_REJECTED: 'document_upload_size_rejected',
  UPLOAD_FORMAT_REJECTED: 'document_upload_format_rejected',
  UPLOAD_PAGE_REJECTED: 'document_upload_page_rejected',
  UPLOAD_SCAN_INFECTED: 'document_upload_scan_infected',
  PIPELINE_STAGE_FAILED: 'document_pipeline_stage_failed',
} as const;

// ─── Sessions ────────────────────────────────────────────────────────────

export const SessionEvents = {
  CREATED: 'session_created',
  UPDATED: 'session_updated',
  DELETED: 'session_deleted',
  VIEWED: 'session_viewed',
  TEMPLATE_SELECTED: 'session_template_selected',
  CREATION_STARTED: 'session_creation_started',
  CREATION_COMPLETED: 'session_creation_completed',
  CREATION_CANCELLED: 'session_creation_cancelled',
  PHASE_CHANGED: 'session_phase_changed',
  GOAL_TOGGLED: 'session_goal_toggled',
  INSIGHT_PINNED: 'session_insight_pinned',
  TAB_SWITCHED: 'session_tab_switched',
  STATUS_CHANGED: 'session_status_changed',
  ENTITY_LINKED: 'session_entity_linked',
  SHARED: 'session_shared',
  CARD_CLICKED: 'session_card_clicked',
  FILTER_APPLIED: 'sessions_filter_applied',
  VIEW_MODE_CHANGED: 'sessions_view_mode_changed',
  SEARCHED: 'sessions_searched',
  SORT_CHANGED: 'sessions_sort_changed',
  SORT_DIRECTION_CHANGED: 'sessions_sort_direction_changed',
} as const;

// ─── Chat ────────────────────────────────────────────────────────────────

export const ChatEvents = {
  CONVERSATION_CREATED: 'chat_conversation_created',
  CONVERSATION_DELETED: 'chat_conversation_deleted',
  CONVERSATION_RENAMED: 'chat_conversation_renamed',
  MESSAGE_SENT: 'chat_message_sent',
  AI_RESPONSE_RECEIVED: 'chat_ai_response_received',
  AI_STREAMING_STARTED: 'chat_ai_streaming_started',
  MESSAGE_FEEDBACK: 'chat_message_feedback',
  MODEL_SELECTED: 'chat_model_selected',
  SCOPE_CHANGED: 'chat_scope_changed',
  EVIDENCE_PANEL_OPENED: 'chat_evidence_panel_opened',
  CITATION_CLICKED: 'chat_citation_clicked',
  AI_PROFILE_CHANGED: 'chat_ai_profile_changed',
} as const;

// ─── Library ─────────────────────────────────────────────────────────────

export const LibraryEvents = {
  DOCUMENT_STARRED: 'library_document_starred',
  DOCUMENT_UNSTARRED: 'library_document_unstarred',
  ADDED_TO_COLLECTION: 'library_added_to_collection',
  REMOVED_FROM_COLLECTION: 'library_removed_from_collection',
  COLLECTION_CREATED: 'library_collection_created',
  COLLECTION_UPDATED: 'library_collection_updated',
  COLLECTION_DELETED: 'library_collection_deleted',
  DOCUMENT_OPENED: 'library_document_opened',
  SEARCH_PERFORMED: 'library_search_performed',
  INTELLIGENCE_PANEL_OPENED: 'library_intelligence_panel_opened',
  CLUSTER_EXPLORED: 'library_cluster_explored',
} as const;

// ─── Citations ───────────────────────────────────────────────────────────

export const CitationEvents = {
  CREATED: 'citation_created',
  UPDATED: 'citation_updated',
  DELETED: 'citation_deleted',
  COPIED: 'citation_copied',
  EXPANDED: 'citation_expanded',
  SORTED: 'citation_sorted',
  STYLE_CHANGED: 'citation_style_changed',
  AI_SUGGESTION_ACCEPTED: 'citation_ai_suggestion_accepted',
  AI_SUGGESTION_REJECTED: 'citation_ai_suggestion_rejected',
  METRICS_VIEWED: 'citation_metrics_viewed',
  DUPLICATE_ALERT_VIEWED: 'citation_duplicate_alert_viewed',
  EXPORTED: 'citation_exported',
} as const;

// ─── Search & Intelligence Bar ───────────────────────────────────────────

export const SearchEvents = {
  INTELLIGENCE_BAR_OPENED: 'intelligence_bar_opened',
  INTELLIGENCE_BAR_CLOSED: 'intelligence_bar_closed',
  QUERY_SUBMITTED: 'search_query_submitted',
  RESULT_CLICKED: 'search_result_clicked',
  MODE_CHANGED: 'search_mode_changed',
  SCOPE_CHANGED: 'search_scope_changed',
  AI_RESPONSE_RECEIVED: 'search_ai_response_received',
  QUICK_ACTION_EXECUTED: 'search_quick_action_executed',
  RECENT_ITEM_CLICKED: 'search_recent_item_clicked',
  COMMAND_PALETTE_OPENED: 'command_palette_opened',
  COMMAND_PALETTE_ACTION: 'command_palette_action_executed',
  RESULT_IMPORTED: 'search_result_imported',
  // End-to-end search observability (FE↔BE correlation via query_id).
  RESULTS_RENDERED: 'search_results_rendered',
  NAVIGATION_SUCCEEDED: 'search_navigation_succeeded',
  NAVIGATION_FAILED: 'search_navigation_failed',
  DEEP_LINK_SUCCEEDED: 'search_deep_link_succeeded',
  DEEP_LINK_FAILED: 'search_deep_link_failed',
  SEARCH_ERROR: 'search_error',
  VIEWER_OPENED: 'search_viewer_opened',
  ARTIFACT_FOCUSED: 'search_artifact_focused',
} as const;

// ─── Settings ────────────────────────────────────────────────────────────

export const SettingsEvents = {
  PROFILE_UPDATED: 'settings_profile_updated',
  AVATAR_CHANGED: 'settings_avatar_changed',
  AI_PREFERENCE_CHANGED: 'settings_ai_preference_changed',
  NOTIFICATIONS_CHANGED: 'settings_notifications_changed',
  PASSWORD_CHANGED: 'settings_password_changed',
  TWO_FA_TOGGLED: 'settings_2fa_toggled',
  SESSION_REVOKED: 'settings_session_revoked',
  DATA_EXPORT_REQUESTED: 'settings_data_export_requested',
  ACCOUNT_DELETE_REQUESTED: 'settings_account_delete_requested',
  INTEGRATION_CONNECTED: 'settings_integration_connected',
  INTEGRATION_DISCONNECTED: 'settings_integration_disconnected',
  API_KEY_CREATED: 'settings_api_key_created',
  API_KEY_REVOKED: 'settings_api_key_revoked',
  SETTINGS_LOADED: 'settings_loaded_from_server',
  SETTINGS_SAVE_FAILED: 'settings_save_failed',
  SETTINGS_HYDRATED: 'settings_hydrated_from_server',
  SECTION_VIEWED: 'settings_section_viewed',
} as const;

// ─── PDF Viewer ──────────────────────────────────────────────────────────

export const PDFEvents = {
  OPENED: 'pdf_opened',
  LOADED: 'pdf_loaded',
  LOAD_ERROR: 'pdf_load_error',
  PAGE_NAVIGATED: 'pdf_page_navigated',
  ZOOMED: 'pdf_zoomed',
  SEARCH_USED: 'pdf_search_used',
  DOWNLOADED: 'pdf_downloaded',
  FULLSCREEN_TOGGLED: 'pdf_fullscreen_toggled',
  PANEL_TOGGLED: 'pdf_panel_toggled',
  OUTLINE_NAVIGATED: 'pdf_outline_navigated',
  ANNOTATION_CREATED: 'pdf_annotation_created',
  PAGE_VIEWED: 'pdf_page_viewed',
  ROTATED: 'pdf_rotated',
  READING_MODE_CHANGED: 'pdf_reading_mode_changed',
  CURSOR_TOOL_CHANGED: 'pdf_cursor_tool_changed',
  PAGE_LAYOUT_CHANGED: 'pdf_page_layout_changed',
  READING_POSITION_RESTORED: 'pdf_reading_position_restored',
  KEYBOARD_SHORTCUT_USED: 'pdf_keyboard_shortcut_used',
  XRAY_TOGGLED: 'pdf_xray_toggled',
  XRAY_ARTIFACT_CLICKED: 'pdf_xray_artifact_clicked',
  XRAY_FILTER_CHANGED: 'pdf_xray_filter_changed',
  XRAY_THRESHOLD_CHANGED: 'pdf_xray_threshold_changed',
  XRAY_KEYBOARD_NAV: 'pdf_xray_keyboard_nav',
  XRAY_FOCUS_TOGGLED: 'pdf_xray_focus_toggled',
  XRAY_LOAD_STARTED: 'pdf_xray_load_started',
  XRAY_LOAD_SUCCESS: 'pdf_xray_load_success',
  XRAY_LOAD_FAILED: 'pdf_xray_load_failed',
  XRAY_LOAD_RETRIED: 'pdf_xray_load_retried',
  XRAY_PIPELINE_REFRESH: 'pdf_xray_pipeline_refresh',
} as const;

// ─── Workspace ───────────────────────────────────────────────────────────

export const WorkspaceEvents = {
  CREATION_STARTED: 'workspace_creation_started',
  CREATED: 'workspace_created',
  CREATION_CANCELLED: 'workspace_creation_cancelled',
  CATEGORY_SELECTED: 'workspace_category_selected',
  SWITCHED: 'workspace_switched',
  MEMBER_INVITED: 'workspace_member_invited',
  CREATE_CLICKED: 'workspace_create_clicked',
  MEMBER_ROLE_CHANGED: 'workspace_member_role_changed',
  MEMBER_REMOVED: 'workspace_member_removed',
  OWNERSHIP_TRANSFERRED: 'workspace_ownership_transferred',
  INVITATION_RESENT: 'workspace_invitation_resent',
  INVITATION_CANCELLED: 'workspace_invitation_cancelled',
  INVITE_DIALOG_OPENED: 'workspace_invite_dialog_opened',
} as const;

// ─── Notes ───────────────────────────────────────────────────────────────

export const NoteEvents = {
  CREATED: 'note_created',
  UPDATED: 'note_updated',
  DELETED: 'note_deleted',
  OPENED: 'note_opened',
  STARRED: 'note_starred',
  CONTENT_SAVED: 'note_content_saved',
  SLASH_COMMAND_USED: 'note_slash_command_used',
  CITATION_INSERTED: 'note_citation_inserted',
  PAPER_REF_INSERTED: 'note_paper_ref_inserted',
  FIGURE_EMBEDDED: 'note_figure_embedded',
  AI_INSIGHT_INSERTED: 'note_ai_insight_inserted',
  FORMATTING_APPLIED: 'note_formatting_applied',
  SEARCH_PERFORMED: 'notes_search_performed',
  SORT_CHANGED: 'notes_sort_changed',
  EXPORTED: 'note_exported',
  EXPORT_FORMAT_SELECTED: 'note_export_format_selected',
} as const;

// ─── Feedback ────────────────────────────────────────────────────────────

export const FeedbackEvents = {
  WIDGET_OPENED: 'feedback_widget_opened',
  WIDGET_DISMISSED: 'feedback_widget_dismissed',
  CATEGORY_SELECTED: 'feedback_category_selected',
  SUBMITTED: 'feedback_submitted',
  INLINE_REACTION: 'feedback_inline_reaction',
  INLINE_TAGS_SUBMITTED: 'feedback_inline_tags_submitted',
  CONTEXTUAL_PROMPT_SHOWN: 'feedback_contextual_prompt_shown',
  CONTEXTUAL_PROMPT_RESPONDED: 'feedback_contextual_prompt_responded',
  CONTEXTUAL_PROMPT_DISMISSED: 'feedback_contextual_prompt_dismissed',
  RESEARCH_TIER_SELECTED: 'feedback_research_tier_selected',
} as const;

// ─── Share ───────────────────────────────────────────────────────────────

export const ShareEvents = {
  LINK_CREATED: 'share_link_created',
  LINK_ACCESSED: 'share_link_accessed',
  LINK_EXPIRED: 'share_link_expired',
  LINK_REVOKED: 'share_link_revoked',
  LINK_NOT_FOUND: 'share_link_not_found',
} as const;

// ─── Discovery ──────────────────────────────────────────────────────────

export const DiscoveryEvents = {
  RESULT_CLICKED: 'discovery_result_clicked',
  VIEW_MODE_CHANGED: 'discovery_view_mode_changed',
  TABLE_SORT_CHANGED: 'discovery_table_sort_changed',
  ROW_SELECTED: 'discovery_row_selected',
  ROW_SAVED: 'discovery_row_saved',
  BULK_SAVE: 'discovery_bulk_save',
  SAVE_DEDUP_HIT: 'discovery_save_dedup_hit',
  QUICK_EVALUATE_OPENED: 'discovery_quick_evaluate_opened',
  EXPORT_CSV: 'discovery_export_csv',
  RELEVANCE_SCORED: 'discovery_relevance_scored',
  SYNTHESIS_STARTED: 'discovery_synthesis_started',
  SYNTHESIS_COMPLETED: 'discovery_synthesis_completed',
  SYNTHESIS_CANCELLED: 'discovery_synthesis_cancelled',
} as const;

// ─── Turnstile (CAPTCHA) ────────────────────────────────────────────────

export const TurnstileEvents = {
  VERIFIED: 'turnstile_verified',
  FAILED: 'turnstile_failed',
  EXPIRED: 'turnstile_expired',
  RETRY: 'turnstile_retry',
  TIMEOUT: 'turnstile_timeout',
} as const;

// ─── Onboarding ─────────────────────────────────────────────────────────

export const OnboardingEvents = {
  STARTED: 'onboarding_started',
  COMPLETED: 'onboarding_completed',
  SKIPPED: 'onboarding_skipped',
  STEP_VIEWED: 'onboarding_step_viewed',
  STEP_COMPLETED: 'onboarding_step_completed',
  STEP_BACK: 'onboarding_step_back',
  WELCOME_CTA_CLICKED: 'onboarding_welcome_cta_clicked',
  OBJECTIVE_TEXT_ENTERED: 'onboarding_objective_entered',
  OBJECTIVE_INTELLIGENCE_SHOWN: 'onboarding_objective_intelligence_shown',
  FIELD_DOMAIN_ROLE_SELECTED: 'onboarding_field_domain_role_selected',
  FIELD_DOMAIN_DOMAIN_SELECTED: 'onboarding_field_domain_domain_selected',
  FIELD_DOMAIN_STAGE_SELECTED: 'onboarding_field_domain_stage_selected',
  FIELD_DOMAIN_AUTO_SUGGESTED: 'onboarding_field_domain_auto_suggested',
  RESEARCH_STYLE_SELECTED: 'onboarding_research_style_selected',
  LIVE_PREVIEW_VIEWED: 'onboarding_live_preview_viewed',
  LIVE_PREVIEW_PAPER_CLICKED: 'onboarding_live_preview_paper_clicked',
  LIVE_PREVIEW_DISCOVERY_FAILED: 'onboarding_live_preview_discovery_failed',
  SYSTEM_INIT_STARTED: 'onboarding_system_init_started',
  SYSTEM_INIT_COMPLETED: 'onboarding_system_init_completed',
  PLAN_VIEWED: 'onboarding_plan_viewed',
  PLAN_SELECTED: 'onboarding_plan_selected',
  FIRST_VALUE_UPLOAD_CLICKED: 'onboarding_fv_upload_clicked',
  FIRST_VALUE_SESSION_STARTED: 'onboarding_fv_session_started',
  FIRST_VALUE_EXPLORE_CLICKED: 'onboarding_fv_explore_clicked',
  FIRST_VALUE_ENTER_WORKSPACE: 'onboarding_fv_enter_workspace',
  COMPLETED_SERVER: 'onboarding_completed_server',
  SKIPPED_SERVER: 'onboarding_skipped_server',
} as const;

// ─── Feed ───────────────────────────────────────────────────────────────

export const FeedEvents = {
  ITEM_SAVED: 'feed_item_saved',
  ITEM_DISMISSED: 'feed_item_dismissed',
  PREFERENCES_UPDATED: 'feed_preferences_updated',
} as const;

// ─── Extraction Tables ──────────────────────────────────────────────────

export const ExtractionEvents = {
  TABLE_CREATED: 'extraction_table_created',
  TABLE_DELETED: 'extraction_table_deleted',
  TABLE_UPDATED: 'extraction_table_updated',
  COLUMN_ADDED: 'extraction_table_column_added',
  COLUMN_REMOVED: 'extraction_table_column_removed',
  DOCUMENTS_ADDED: 'extraction_table_documents_added',
  EXTRACTION_TRIGGERED: 'extraction_table_extraction_triggered',
  CSV_EXPORTED: 'extraction_table_csv_exported',
  CREATED_FROM_SEARCH: 'extraction_table_created_from_search',
} as const;

// ─── Reader (PDF text selection) ────────────────────────────────────────

export const ReaderEvents = {
  SELECTION_NOTE: 'reader_selection_note',
  SELECTION_CITE: 'reader_selection_cite',
  SELECTION_ASK: 'reader_selection_ask',
} as const;

// ─── Evidence Panel ─────────────────────────────────────────────────────

export const EvidenceEvents = {
  DEEP_LINK_CLICKED: 'evidence_deep_link_clicked',
  PINNED: 'evidence_pinned',
  UNPINNED: 'evidence_unpinned',
  FIND_SIMILAR_CLICKED: 'evidence_find_similar_clicked',
} as const;

// ─── Knowledge Graph ────────────────────────────────────────────────────

export const GraphEvents = {
  NODE_SELECTED: 'graph_node_selected',
  EDGE_FILTER_CHANGED: 'graph_edge_filter_changed',
  NODE_SAVED: 'graph_node_saved',
} as const;

// ─── Document Intelligence Panels ───────────────────────────────────────

export const IntelligenceEvents = {
  RECOMMENDATION_CLICKED: 'intelligence_recommendation_clicked',
  CONTRADICTION_VIEWED: 'intelligence_contradiction_viewed',
  CITATION_GRAPH_VIEWED: 'intelligence_citation_graph_viewed',
  EVIDENCE_SYNTHESIS_VIEWED: 'intelligence_evidence_synthesis_viewed',
  RESEARCH_GAP_VIEWED: 'intelligence_research_gap_viewed',
} as const;

// ─── Search (extended) ──────────────────────────────────────────────────

export const SearchExtendedEvents = {
  METHODOLOGY_PREFILTER_TOGGLED: 'search_methodology_prefilter_toggled',
  METHODOLOGY_PREFILTER_CLEARED: 'search_methodology_prefilter_cleared',
} as const;

// ─── Billing ─────────────────────────────────────────────────────────────

export const BillingEvents = {
  CHECKOUT_INITIATED: 'billing_checkout_initiated',
  PORTAL_OPENED: 'billing_portal_opened',
  ADDON_PURCHASED: 'billing_addon_purchased',
  ADDON_CANCELLED: 'billing_addon_cancelled',
  SEAT_UPDATED: 'billing_seat_updated',
  PLAN_COMPARISON_UPGRADE_CLICKED: 'billing_plan_comparison_upgrade_clicked',
  PLAN_COMPARISON_INTERVAL_TOGGLED: 'billing_plan_comparison_interval_toggled',
  PLAN_COMPARISON_CONTACT_SALES_CLICKED: 'billing_plan_comparison_contact_sales_clicked',
} as const;
